import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import store, { bfiUserActions, userActions, brokerActions } from '@apply/adminStore';
import { useBrokerDetailsStore } from '../../state/brokerDetailsStore';
import { useSaveBrokerMutation, useActivateBrokerMutation, useRejectBrokerMutation, BrokerMutationError } from '../../queries/brokerDetailsQueries';
import { RefreshEvents } from '../../../../common/SharedEvents';
import { LoginApi } from '@apply/api';
import { applicationInsights } from '../../../../ApplicationInsights'
import { BrokerStatusType } from '@apply/constants';
import { BrokerApi } from '@apply/api';
import { handleAdminError } from '../../../../common/functions/handleAdminError';
import { handleAdminWarning } from '../../../../common/SharedFunctions';
import { snackbarError } from '../../../../common/functions/snackbarFunctions';
import { useWaitOverlayStore } from '@apply/components';

const refreshEvents = new RefreshEvents();

interface ModalControls {
    setConfirmSubmitOpen: (open: boolean) => void;
    setRejectConfirmOpen: (open: boolean) => void;
}

export interface UseBrokerAccountSetupSubmitReturn {
    handleSave: () => Promise<void>;
    handleActivate: () => Promise<void>;
    handleReject: (comment: string) => Promise<void>;
    confirmSubmit: () => Promise<void>;
    isSubmitting: boolean;
}

const buildSaveRequest = (s: ReturnType<typeof useBrokerDetailsStore.getState>, ipAddress: string): BrokerApi.IUpdateBrokerPost => {
    return {
        id: s.id || undefined,
        firstName: s.firstName,
        lastName: s.lastName,
        arizonaLicenseNumber: s.arizonaLicenseNumber,
        wireBankName: s.wireBankName,
        wireRecipientName: s.wireRecipientName,
        wireAccountLastFourDigits: s.wireAccountLastFourDigits,
        iccaTemplateId: s.iccaTemplateId,
        clientFeeType: s.clientFeeType,
        pointOfContactId: s.pointOfContactId,
        hasSubmittedW9: s.hasSubmittedW9,
        w9Url: s.w9Url,
        w9File: s.w9File || undefined,
        businessName: s.businessName,
        businessState: s.businessState,
        title: s.title,
        isFlagged: s.brokerFlagged,
        backgroundCheckDate: s.backgroundCheckDate ?? undefined,
        acceptedTos: s.eSignAgreement,
        fontId: s.eSignFont,
        canSendTextMessages: s.canSendTextMessages,
        ipAddress: ipAddress || undefined,
        businessAddress: s.businessAddress,
        homeAddress: s.sameAsContactAddress ? s.businessAddress : s.homeAddress,
        phoneNumberA: s.phoneNumberA,
        phoneNumberAType: s.phoneNumberAType,
        phoneNumberB: s.phoneNumberB,
        phoneNumberBType: s.phoneNumberBType,
        email: s.email,
        secondaryEmail: s.secondaryEmail,
        dateOfBirth: s.dateOfBirth,
    };
};

export const useBrokerAccountSetupSubmit = (
    validate: () => { isValid: boolean },
    { setConfirmSubmitOpen, setRejectConfirmOpen }: ModalControls,
): UseBrokerAccountSetupSubmitReturn => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    // Redux state is typed as any
    const bfiUser = useSelector((state: any) => state.bfiUser);
    const user = useSelector((state: any) => state.user);
    /* eslint-enable @typescript-eslint/no-explicit-any */

    const { dispatch } = store;

    const navigate = useNavigate();
    const saveBrokerMutation = useSaveBrokerMutation();
    const activateBrokerMutation = useActivateBrokerMutation();
    const rejectBrokerMutation = useRejectBrokerMutation();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const pendingRequest = useRef<BrokerApi.IUpdateBrokerPost | null>(null);

    const _getIpAddress = async (): Promise<string> => {
        if (!useBrokerDetailsStore.getState().isBroker) return '';
        try {
            const loginApi = new LoginApi.LoginApi(applicationInsights);
            const response = await loginApi.getIpAddress();
            return response.statusCode === 200 ? response.result : '';
        } catch {
            return '';
        }
    };

    const _closeForm = () => {
        dispatch(brokerActions.setBrokerModalOpen({ openBrokerModal: false }));
    };

    const _onSaveSuccess = () => {
        const s = useBrokerDetailsStore.getState();
        if (s.isBroker) {
            dispatch(bfiUserActions.bfiUserUpdate({ ...bfiUser, areTermsAccepted: true, requiresESign: false }));
            dispatch(userActions.userUpdate({ ...user, areTermsAccepted: true, requiresESign: false }));
        }
        refreshEvents.dispatchRefreshEvent();
    };

    const _executeSave = async (request: BrokerApi.IUpdateBrokerPost): Promise<boolean> => {
        const { setWaiting } = useWaitOverlayStore.getState();
        try {
            setIsSubmitting(true);
            setWaiting(true, 'Saving...');
            await saveBrokerMutation.mutateAsync({
                data: request,
                isAdmin: useBrokerDetailsStore.getState().isAdmin,
            });
            return true;
        } catch (ex) {
            const error = ex instanceof Error ? ex : new Error(String(ex));
            if (ex instanceof BrokerMutationError) {
                if (ex.type === 'BROKER_UPDATE_FAILED') {
                    handleAdminError(error,
                        'There was an error submitting your request, please refresh your browser and try again. If the error persists, please contact team@builderfinance.com.');
                } else if (ex.type === 'BROKER_DUPLICATE') {
                    handleAdminError(error,
                        'The Email Address that you have entered already exists in the database.');
                }
            } else {
                handleAdminError(error, 'An unexpected error has occurred. Please try again.');
            }
            return false;
        } finally {
            setIsSubmitting(false);
            setWaiting(false);
        }
    };

    const _buildRequest = async () => {
        const ipAddress = await _getIpAddress();
        return buildSaveRequest(useBrokerDetailsStore.getState(), ipAddress);
    };

    const _saveCloseAndNotifyW9 = async (request: BrokerApi.IUpdateBrokerPost) => {
        if (!await _executeSave(request)) return;
        _onSaveSuccess();

        const s = useBrokerDetailsStore.getState();
        if (s.isBroker && !(s.hasSubmittedW9 || s.w9File || s.w9FileName)) {
            snackbarError('We need a copy of your W-9, please send it to the following email address: referral@builderfinance.com');
        }

        _closeForm();

        if (s.isBroker) {
            navigate('/referrals');
        }
    };

    const handleSave = async () => {
        if (!validate().isValid) {
            handleAdminWarning('Please fix the invalid fields.');
            return;
        }

        const request = await _buildRequest();
        const { isAdmin, isBroker } = useBrokerDetailsStore.getState();

        if (isAdmin) {
            await _saveCloseAndNotifyW9(request);
        } else if (isBroker) {
            pendingRequest.current = request;
            setConfirmSubmitOpen(true);
        }
    };

    const confirmSubmit = async () => {
        const request = pendingRequest.current;
        if (!request) return;
        pendingRequest.current = null;
        setConfirmSubmitOpen(false);
        await _saveCloseAndNotifyW9(request);
    };

    const handleActivate = async () => {
        if (!validate().isValid) {
            handleAdminWarning('Please fix the invalid fields.');
            return;
        }
        const { setWaiting } = useWaitOverlayStore.getState();

        try {
            const request = await _buildRequest();
            if (!await _executeSave(request)) return;

            setIsSubmitting(true);
            setWaiting(true, 'Activating broker...');
            const s = useBrokerDetailsStore.getState();
            await activateBrokerMutation.mutateAsync({
                brokerId: s.brokerId,
                isReactivation: s.brokerStatus === BrokerStatusType.Rejected,
            });

            _onSaveSuccess();
            _closeForm();
        } catch (ex) {
            handleAdminError(ex instanceof Error ? ex : new Error(String(ex)), 'An unexpected error has occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
            setWaiting(false);
        }
    };

    const handleReject = async (comment: string) => {
        setRejectConfirmOpen(false);
        if (!validate().isValid) {
            handleAdminWarning('Please fix the invalid fields.');
            return;
        }
        const { setWaiting } = useWaitOverlayStore.getState();

        try {
            const request = await _buildRequest();
            if (!await _executeSave(request)) return;

            setIsSubmitting(true);
            setWaiting(true, 'Rejecting broker...');
            await rejectBrokerMutation.mutateAsync({
                brokerId: useBrokerDetailsStore.getState().brokerId,
                comment,
            });

            _onSaveSuccess();
            _closeForm();
        } catch (ex) {
            handleAdminError(ex instanceof Error ? ex : new Error(String(ex)), 'An unexpected error has occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
            setWaiting(false);
        }
    };

    return {
        handleSave,
        handleActivate,
        handleReject,
        confirmSubmit,
        isSubmitting,
    };
};
